import type { Membership, MembershipType } from '@kalos/kalos-rpc';

import type { ContactOption } from '../../components/ContactPicker';

export type DerivedStatus =
  | 'active'
  | 'admin_review'
  | 'awaiting_confirmation'
  | 'not_active_yet'
  | 'pending_payment'
  | 'cancelled'
  | 'scheduled_cancel'
  | 'renewed'
  | 'expired'
  | 'unknown';
export type PendingReason = 'awaiting_confirmation' | 'renewal_due' | 'payment_failed';

/** One label per status, so a badge and a status control can't word the same state differently. */
export const STATUS_LABELS: Record<DerivedStatus, string> = {
  active: 'Active',
  admin_review: 'Admin Review',
  awaiting_confirmation: 'Awaiting Confirmation',
  not_active_yet: 'Not active yet',
  pending_payment: 'Pending Payment',
  scheduled_cancel: 'Scheduled Cancel',
  cancelled: 'Cancelled',
  renewed: 'Renewed',
  expired: 'Expired',
  unknown: 'Unknown',
};
export type ContractKind = 'catalog' | 'custom';
export type PmVisitStatus = 'scheduled' | 'completed' | 'unscheduled';

export interface PmVisit {
  eventId?: number;
  jobNumber?: string;
  date?: string;
  status: PmVisitStatus;
}

export const STATUS_TABS = [
  'all',
  'active',
  'admin_review',
  'awaiting_confirmation',
  'pending_payment',
  'expired',
  'cancelled',
] as const;
export const TYPE_FILTERS = ['all', 'residential', 'commercial'] as const;

export type StatusTab = (typeof STATUS_TABS)[number];
export type TypeFilter = (typeof TYPE_FILTERS)[number];

/** Generic anchor membership_type for custom (commercial) plans; value lives in instance lines. */
export const CUSTOM_TYPE_NAME = 'Custom';

export interface MembershipSubRow {
  membership: Membership;
  membershipType: MembershipType | undefined;
  propertyLabel: string;
  planLabel: string;
  pricePerYear: number;
  /** Commercial (custom) membership — value comes from frozen lines, not the catalog sticker. */
  isCommercial: boolean;
  status: DerivedStatus;
  pendingReason?: PendingReason;
  cancellationDate?: string;
  scheduledCancelDate?: string;
  slotTotal: number;
  /** Scheduled PM jobs for this membership, capped at slotTotal (same source as the contract rollup). */
  scheduledCount: number;
  /** Earliest upcoming PM visit date (YYYY-MM-DD), if any; from the same PM events as the rollup. */
  nextVisitDate?: string;
  /** This membership's own payment method (may differ from the contract-level Payment column). */
  paymentMethod: string;
  /** Owes money a CSR has to collect by hand — enrollment upfront or a renewal now due. */
  awaitingManualPayment: boolean;
  /** The amendment this plan was added on still awaits review or payment. */
  amendmentOpen: boolean;
}

export interface ContractRow {
  contractId: number;
  /** Contract customer's user id (cards on file + subscription enrollment). 0 = unknown. */
  customerUserId: number;
  /** contract_billing_type id: 1 = helcim_auto, 2 = manual, 0 = unset. */
  billingTypeId: number;
  /** Helcim subscription the contract autobills through; 0 = none. */
  helcimSubscriptionId: number;
  customerLabel: string;
  customerEmail: string;
  /** Every address on file for this contract's customer and properties, for the invoice send picker. */
  recipientOptions: ContactOption[];
  contractNumber: string;
  kind: ContractKind;
  /** Contract-level plan label: 'Custom' (commercial), the shared catalog name, or 'Multiple'. */
  planLabel: string;
  /** Derived signal (not persisted): residential contract with a filter over 1 inch deep on file —
   * auto-suggests the customer may want a media filter. */
  wantsMediaFilter: boolean;
  departmentId: number;
  propertyCount: number;
  status: DerivedStatus;
  /** Admin Review tab flag: the contract or one of its added plans awaits review. Independent of
   * `status`, which an unpaid contract wins. */
  needsReview: boolean;
  /** Added plans (amendments) awaiting review or payment. */
  openAddCount: number;
  addReviewCount: number;
  addUnpaidCount: number;
  paymentMethod?: string;
  contractStart: string | undefined;
  /** Contract creation date, shown in the drawer header. */
  contractCreated: string | undefined;
  /** Sold-by employee name, from contract.sold_by_user_id (fallback: membership created_by_user_id). */
  soldByLabel: string | undefined;
  /** Sold-by employee id (for the editable drawer picker); 0 = unset. */
  soldByUserId: number;
  /** Contract payment terms (e.g. Net 30), stored in contract.paymentTerms ('' = unset). */
  paymentTerms: string;
  /** PO / reference number, stored in contract.po_number ('' = unset). */
  poNumber: string;
  renewalDate: string | undefined;
  annualValue: number;
  // Scheduling rollup: scheduled PM visits over total PM slots (non-cancelled coverage).
  scheduledCount: number;
  slotTotalAll: number;
  // True while the contract's PM-event rollup is still loading or its fetch failed — render "Loading…", not a false 0.
  schedulingUnavailable: boolean;
  // Billing rollup, aggregated from the contract's invoices (collected = invoices marked Paid).
  invoicedTotal: number;
  collectedTotal: number;
  outstandingTotal: number;
  // True when the contract's invoice fetch failed — billing is unknown, not zero.
  billingUnavailable: boolean;
  notes: string;
  memberships: MembershipSubRow[];
}

export type PaymentMethodFilter = string;

export interface StatusTabDef {
  value: StatusTab;
  label: string;
}

// Tab filtering happens server-side (applyTab in useMembershipsDashboard); these are display defs.
export const STATUS_TAB_DEFS: StatusTabDef[] = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'admin_review', label: 'Admin Review' },
  { value: 'awaiting_confirmation', label: 'Awaiting Confirmation' },
  { value: 'pending_payment', label: 'Pending Payment' },
  { value: 'expired', label: 'Expired' },
  { value: 'cancelled', label: 'Cancelled' },
];
